import React, { useState, useEffect, useCallback } from "react";
import { Bell, BellOff, BellRing, Loader2, Smartphone, Info, Moon, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Convert a base64 VAPID public key to a Uint8Array for pushManager.subscribe.
 */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
  return arr;
}

function isIos() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}

/** Format hour:minute as "10:00 PM" */
function fmtTime(h, m) {
  const suffix = h >= 12 ? "PM" : "AM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, "0")} ${suffix}`;
}

export function PushNotificationCard({ variant = "card" }) {
  const [status, setStatus] = useState("loading"); // loading | unsupported | denied | prompt | subscribed | unsubscribed
  const [saving, setSaving] = useState(false);
  const [vapidKey, setVapidKey] = useState("");
  const [sheetOpen, setSheetOpen] = useState(false);

  // Quiet hours state
  const [qhEnabled, setQhEnabled] = useState(false);
  const [qhStartHour, setQhStartHour] = useState(22);
  const [qhStartMinute, setQhStartMinute] = useState(0);
  const [qhEndHour, setQhEndHour] = useState(7);
  const [qhEndMinute, setQhEndMinute] = useState(0);
  const [qhSaving, setQhSaving] = useState(false);
  const [qhLoaded, setQhLoaded] = useState(false);

  const checkStatus = useCallback(async () => {
    // Check browser support
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      if (isIos() && !isStandalone()) {
        setStatus("ios-needs-homescreen");
        return;
      }
      setStatus("unsupported");
      return;
    }
    // Check Notification permission
    const perm = Notification.permission;
    if (perm === "denied") {
      setStatus("denied");
      return;
    }
    // Fetch VAPID key
    try {
      const { data } = await api.get("/push/vapid-key");
      if (!data.vapid_public_key) {
        setStatus("unsupported");
        return;
      }
      setVapidKey(data.vapid_public_key);
    } catch {
      setStatus("unsupported");
      return;
    }
    // Check backend subscription status
    try {
      const { data } = await api.get("/push/status");
      if (data.subscribed) {
        setStatus("subscribed");
      } else if (perm === "granted") {
        setStatus("unsubscribed");
      } else {
        setStatus("prompt");
      }
    } catch {
      setStatus(perm === "granted" ? "unsubscribed" : "prompt");
    }
  }, []);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  // Load quiet hours when sheet opens
  const loadQuietHours = useCallback(async () => {
    if (qhLoaded) return;
    try {
      const { data } = await api.get("/push/quiet-hours");
      setQhEnabled(data.enabled || false);
      setQhStartHour(data.start_hour ?? 22);
      setQhStartMinute(data.start_minute ?? 0);
      setQhEndHour(data.end_hour ?? 7);
      setQhEndMinute(data.end_minute ?? 0);
      setQhLoaded(true);
    } catch {
      // Defaults are fine
      setQhLoaded(true);
    }
  }, [qhLoaded]);

  const saveQuietHours = async (enabled, sh, sm, eh, em) => {
    setQhSaving(true);
    try {
      await api.put("/push/quiet-hours", {
        enabled,
        start_hour: sh,
        start_minute: sm,
        end_hour: eh,
        end_minute: em,
      });
      toast.success(enabled ? `Quiet hours set: ${fmtTime(sh, sm)} – ${fmtTime(eh, em)}` : "Quiet hours disabled");
    } catch {
      toast.error("Could not save quiet hours");
    } finally {
      setQhSaving(false);
    }
  };

  const enableNotifications = async () => {
    setSaving(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        setStatus("denied");
        toast.error("Notification permission was denied. You can change this in your browser settings.");
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });
      const subJson = subscription.toJSON();
      await api.post("/push/subscribe", {
        endpoint: subJson.endpoint,
        keys: { p256dh: subJson.keys.p256dh, auth: subJson.keys.auth },
      });
      setStatus("subscribed");
      toast.success("Notifications enabled! You'll be notified about new news and events.");
    } catch (err) {
      console.error("[Push] enable error:", err);
      toast.error("Could not enable notifications. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const disableNotifications = async () => {
    setSaving(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const subscription = await reg.pushManager.getSubscription();
      if (subscription) {
        await api.delete("/push/unsubscribe", { params: { endpoint: subscription.endpoint } });
        await subscription.unsubscribe();
      }
      setStatus("unsubscribed");
      setSheetOpen(false);
      toast.success("Notifications disabled.");
    } catch (err) {
      console.error("[Push] disable error:", err);
      toast.error("Could not disable notifications.");
    } finally {
      setSaving(false);
    }
  };

  const sendTestPush = async () => {
    try {
      await api.post("/push/test");
      toast.success("Test notification sent! Check your device.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send test notification.");
    }
  };

  if (status === "loading") return null;

  // ---- Settings Sheet (shared by both variants) ----
  const settingsSheet = (
    <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
      <SheetContent side="bottom" className="rounded-t-2xl max-h-[80vh]" data-testid="push-settings-sheet">
        <SheetHeader className="pb-2">
          <SheetTitle className="flex items-center gap-2 text-base">
            <BellRing className="h-4 w-4 text-[hsl(var(--accent))]" />
            Notification Settings
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-5 pb-6">
          {/* Status */}
          <div className="flex items-center gap-3 px-1">
            <div className="h-8 w-8 rounded-lg bg-[hsl(142_42%_34%)]/10 text-[hsl(142_42%_34%)] flex items-center justify-center">
              <BellRing className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium">Notifications Active</div>
              <div className="text-xs text-muted-foreground">You'll be notified about news, events, and team selections</div>
            </div>
          </div>

          <Separator />

          {/* Quiet Hours */}
          <div className="space-y-3">
            <div className="flex items-center justify-between px-1">
              <div className="flex items-center gap-2">
                <Moon className="h-4 w-4 text-muted-foreground" />
                <div>
                  <div className="text-sm font-medium">Quiet Hours</div>
                  <div className="text-xs text-muted-foreground">Silence notifications during set times</div>
                </div>
              </div>
              <Switch
                checked={qhEnabled}
                onCheckedChange={(checked) => {
                  setQhEnabled(checked);
                  saveQuietHours(checked, qhStartHour, qhStartMinute, qhEndHour, qhEndMinute);
                }}
                disabled={qhSaving}
                data-testid="quiet-hours-toggle"
              />
            </div>

            {qhEnabled && (
              <div className="bg-[hsl(var(--secondary))]/50 rounded-xl p-3 space-y-3" data-testid="quiet-hours-times">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Clock className="h-3.5 w-3.5" />
                  <span>No notifications between:</span>
                </div>
                <div className="flex items-center gap-2">
                  {/* Start time */}
                  <Select
                    value={String(qhStartHour)}
                    onValueChange={(v) => {
                      const h = parseInt(v, 10);
                      setQhStartHour(h);
                      saveQuietHours(true, h, qhStartMinute, qhEndHour, qhEndMinute);
                    }}
                  >
                    <SelectTrigger className="w-[72px] h-9 text-xs" data-testid="quiet-start-hour">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 24 }, (_, i) => (
                        <SelectItem key={i} value={String(i)}>{fmtTime(i, 0).replace(/:00/, "")}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={String(qhStartMinute)}
                    onValueChange={(v) => {
                      const m = parseInt(v, 10);
                      setQhStartMinute(m);
                      saveQuietHours(true, qhStartHour, m, qhEndHour, qhEndMinute);
                    }}
                  >
                    <SelectTrigger className="w-[60px] h-9 text-xs" data-testid="quiet-start-minute">
                      <SelectValue>{String(qhStartMinute).padStart(2, "0")}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {[0, 15, 30, 45].map((m) => (
                        <SelectItem key={m} value={String(m)}>{String(m).padStart(2, "0")}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <span className="text-xs text-muted-foreground font-medium">to</span>

                  {/* End time */}
                  <Select
                    value={String(qhEndHour)}
                    onValueChange={(v) => {
                      const h = parseInt(v, 10);
                      setQhEndHour(h);
                      saveQuietHours(true, qhStartHour, qhStartMinute, h, qhEndMinute);
                    }}
                  >
                    <SelectTrigger className="w-[72px] h-9 text-xs" data-testid="quiet-end-hour">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 24 }, (_, i) => (
                        <SelectItem key={i} value={String(i)}>{fmtTime(i, 0).replace(/:00/, "")}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={String(qhEndMinute)}
                    onValueChange={(v) => {
                      const m = parseInt(v, 10);
                      setQhEndMinute(m);
                      saveQuietHours(true, qhStartHour, qhStartMinute, qhEndHour, m);
                    }}
                  >
                    <SelectTrigger className="w-[60px] h-9 text-xs" data-testid="quiet-end-minute">
                      <SelectValue>{String(qhEndMinute).padStart(2, "0")}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {[0, 15, 30, 45].map((m) => (
                        <SelectItem key={m} value={String(m)}>{String(m).padStart(2, "0")}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-[10px] text-muted-foreground italic">
                  Times are in local Hobart time (AEST/AEDT)
                </div>
              </div>
            )}
          </div>

          <Separator />

          {/* Actions */}
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={sendTestPush}
              className="flex-1 min-h-[44px] rounded-full"
              data-testid="push-test-btn"
            >
              <Bell className="h-4 w-4 mr-2" />
              Send Test
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={saving}
              onClick={disableNotifications}
              className="flex-1 min-h-[44px] rounded-full text-muted-foreground"
              data-testid="push-disable-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <BellOff className="h-4 w-4 mr-2" />}
              Disable
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );

  // ---- Tile variant (quick-action grid) ----
  if (variant === "tile") {
    if (status !== "subscribed") return null;
    return (
      <>
        <div
          className="group relative cursor-pointer"
          data-testid="push-notification-card"
          onClick={() => { setSheetOpen(true); loadQuietHours(); }}
        >
          <Card className="p-2.5 h-full rounded-xl hover:border-foreground/40 transition-all hover-lift bg-[#faf8f5]">
            <div className="flex items-start justify-between">
              <div className="h-7 w-7 rounded-md bg-[hsl(var(--secondary))] text-[hsl(142_42%_34%)] flex items-center justify-center group-hover:bg-[hsl(142_42%_34%)] group-hover:text-white transition-colors">
                <BellRing className="h-3.5 w-3.5" />
              </div>
              {qhEnabled && (
                <Moon className="h-3 w-3 text-muted-foreground" />
              )}
            </div>
            <div className="mt-2 font-semibold tracking-tight text-xs">Notifications</div>
            <div className="text-[10px] text-muted-foreground mt-0.5 leading-tight">
              {qhEnabled ? "Quiet hours on" : "Active"}
            </div>
          </Card>
        </div>
        {settingsSheet}
      </>
    );
  }

  // ---- Full card variant (non-subscribed states) ----
  if (status === "subscribed") return null;

  return (
    <Card className="overflow-hidden border border-[hsl(var(--border))] bg-[#faf8f5]" data-testid="push-notification-card">
      <div className="px-4 py-3 border-b border-[hsl(var(--border))]" style={{ background: "linear-gradient(135deg, #1E3A8A 0%, #2749C9 55%, #4169E1 100%)" }}>
        <div className="flex items-center gap-2">
          <BellRing className="h-4 w-4 text-white/80" />
          <span className="font-semibold text-sm text-white">Push Notifications</span>
        </div>
      </div>

      <div className="px-4 py-4 space-y-3">
        {/* iOS Home Screen guidance */}
        {status === "ios-needs-homescreen" && (
          <div className="space-y-2" data-testid="push-ios-help">
            <div className="flex items-start gap-2">
              <Smartphone className="h-5 w-5 text-[hsl(var(--accent))] mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium">Add to Home Screen Required</p>
                <p className="text-xs text-muted-foreground mt-1">
                  On iPhones and iPads, push notifications require this app to be added to your Home Screen first.
                </p>
                <ol className="text-xs text-muted-foreground mt-2 space-y-1 list-decimal list-inside">
                  <li>Tap the <strong>Share</strong> button (square with arrow) in Safari</li>
                  <li>Scroll down and tap <strong>"Add to Home Screen"</strong></li>
                  <li>Open the app from your Home Screen</li>
                  <li>Come back here to enable notifications</li>
                </ol>
              </div>
            </div>
          </div>
        )}

        {/* Unsupported browser */}
        {status === "unsupported" && (
          <div className="flex items-start gap-2" data-testid="push-unsupported">
            <Info className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
            <p className="text-xs text-muted-foreground">
              Push notifications are not supported on this browser. Try Chrome, Firefox, Edge, or Safari 16+.
            </p>
          </div>
        )}

        {/* Permission denied */}
        {status === "denied" && (
          <div className="flex items-start gap-2" data-testid="push-denied">
            <BellOff className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium">Notifications Blocked</p>
              <p className="text-xs text-muted-foreground mt-1">
                You previously blocked notifications. To enable them, go to your browser settings and allow notifications for this site.
              </p>
            </div>
          </div>
        )}

        {/* Ready to enable */}
        {(status === "prompt" || status === "unsubscribed") && (
          <div className="space-y-3" data-testid="push-prompt">
            <div className="flex items-start gap-2">
              <Bell className="h-4 w-4 text-[hsl(var(--accent))] mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium">Stay Updated</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Get notified on your device when new news articles or events are published. Works even when the app is closed.
                </p>
              </div>
            </div>
            <Button
              size="sm"
              disabled={saving}
              onClick={enableNotifications}
              className="w-full min-h-[44px] rounded-full bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white"
              data-testid="push-enable-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Bell className="h-4 w-4 mr-2" />}
              Enable Notifications
            </Button>
          </div>
        )}

        {/* Currently subscribed — handled by early return above, this is a fallback */}
      </div>
    </Card>
  );
}
